import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from "react-dom";

//react-tooltip-lite 는 처음 hover 할 때 body 에 div 를 직접 붙이고, 언마운트에서 직접 떼어낸다.
//React 18 은 Suspense 가 화면을 숨길 때도 언마운트를 호출해서, 실제 언마운트에서 두 번째 removeChild 가 나 앱 전체가 죽었다.
//hover 중일 때만 createPortal 로 렌더해 DOM 추가·제거를 React 가 직접 하도록 한다.
//스타일은 기존 react-tooltip-lite 와 같은 클래스명(react-tooltip-lite, react-tooltip-lite-arrow)으로 지정한다.

const BODY_PADDING = 10;
const ARROW_SIZE = 10;
const HIDDEN_POSITION = -10000000;

const getPosition = (targetRect, tip, direction, distance) => {
    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const spaceAbove = targetRect.top;
    const spaceBelow = window.innerHeight - targetRect.bottom;

    //지정한 방향에 공간이 없고 반대쪽이 더 넓으면 뒤집는다.
    let realDirection = direction;
    if (direction === 'up' && spaceAbove < tipHeight + distance && spaceBelow > spaceAbove) realDirection = 'down';
    if (direction === 'down' && spaceBelow < tipHeight + distance && spaceAbove > spaceBelow) realDirection = 'up';

    const centerX = targetRect.left + targetRect.width / 2;
    const maxLeft = viewportWidth - BODY_PADDING - tipWidth;
    const left = Math.max(Math.min(centerX - tipWidth / 2, maxLeft), BODY_PADDING);
    const top = realDirection === 'up' ? targetRect.top - distance - tipHeight : targetRect.bottom + distance;
    const arrowTop = realDirection === 'up' ? targetRect.top - distance : targetRect.bottom + distance - ARROW_SIZE;

    return {
        realDirection,
        top: top + window.scrollY,
        left: left + window.scrollX,
        arrowTop: arrowTop + window.scrollY,
        arrowLeft: centerX - ARROW_SIZE + window.scrollX,
    };
};

const HoverTooltip = ({
                          children,
                          content,
                          className,
                          direction = 'up',
                          distance = 15,
                          arrow = true,
                          arrowColor,
                          hoverDelay = 50,
                          zIndex = 1000,
                          color,
                          padding = '10px',
                      }) => {
    const targetRef = useRef(null);
    const tipRef = useRef(null);
    const timerRef = useRef(null);
    const [targetRect, setTargetRect] = useState(null);
    const [position, setPosition] = useState(null);

    const show = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (targetRef.current) {
                setTargetRect(targetRef.current.getBoundingClientRect());
            }
        }, hoverDelay);
    }, [hoverDelay]);

    const hide = useCallback(() => {
        clearTimeout(timerRef.current);
        setTargetRect(null);
        setPosition(null);
    }, []);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    //먼저 화면 밖에 그려 크기를 잰 뒤, 페인트 전에 실제 위치로 옮긴다.
    useLayoutEffect(() => {
        if (targetRect && tipRef.current) {
            setPosition(getPosition(targetRect, tipRef.current, direction, distance));
        }
    }, [targetRect, direction, distance, content]);

    const isOpen = targetRect !== null && Boolean(content);

    return (
        <div className={className} style={{position: 'relative'}} ref={targetRef}
             onMouseEnter={show} onMouseLeave={hide}>
            {children}
            {isOpen && createPortal(
                <div className={className}>
                    <span className={'react-tooltip-lite'} ref={tipRef} style={{
                        position: 'absolute',
                        display: 'inline-block',
                        boxSizing: 'border-box',
                        top: position ? position.top : HIDDEN_POSITION,
                        left: position ? position.left : HIDDEN_POSITION,
                        zIndex,
                        color,
                        padding,
                    }}>
                        {content}
                    </span>
                    {arrow && position &&
                        <span className={'react-tooltip-lite-arrow'} style={{
                            position: 'absolute',
                            width: 0,
                            height: 0,
                            top: position.arrowTop,
                            left: position.arrowLeft,
                            zIndex: zIndex + 1,
                            borderLeft: `${ARROW_SIZE}px solid transparent`,
                            borderRight: `${ARROW_SIZE}px solid transparent`,
                            [position.realDirection === 'up' ? 'borderTop' : 'borderBottom']: `${ARROW_SIZE}px solid ${arrowColor || 'currentColor'}`,
                        }}/>
                    }
                </div>,
                document.body
            )}
        </div>
    );
};

export default HoverTooltip;
